"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.default = buildMatrixTimeline;
require("regenerator-runtime/runtime");
// setup
const helpers_1 = require("../shared/helpers");
const instructions_1 = require("./trials/instructions");
const downexStimulus_1 = require("./trials/downexStimulus");
const taskSetup_1 = require("../taskSetup");
// trials
const trials_1 = require("../shared/trials");
const config_1 = require("./helpers/config");
const repeatInstructions_1 = require("../shared/trials/repeatInstructions");
const prepareCat_1 = require("../shared/helpers/prepareCat");
const taskStore_1 = require("../../taskStore");
const batchPreloading_1 = require("../shared/helpers/batchPreloading");
function buildMatrixTimeline(config, mediaAssets) {
    (0, helpers_1.initTrialSaving)(config);
    const initialTimeline = (0, helpers_1.initTimeline)(config, trials_1.enterFullscreen);
    const ifRealTrialResponse = {
        timeline: [(0, trials_1.getAudioResponse)(mediaAssets)],
        conditional_function: () => {
            const stim = (0, taskStore_1.taskStore)().nextStimulus;
            if (runCat) {
                return true;
            }
            if (stim.assessmentStage === 'practice_response' || stim.trialType === 'instructions') {
                return false;
            }
            return true;
        },
    };
    const defaultCorpus = (0, taskStore_1.taskStore)().corpora.stimulus;
    const downexCorpus = (0, taskStore_1.taskStore)().corpora.downex;
    const fullCorpus = [...downexCorpus, ...defaultCorpus];
    const translations = (0, taskStore_1.taskStore)().translations;
    const validationErrorMap = {};
    const { semThreshold, heavyInstructions, runCat } = (0, taskStore_1.taskStore)();
    const layoutConfigMap = {};
    let i = 0;
    for (const c of heavyInstructions ? fullCorpus : defaultCorpus) {
        const { itemConfig, errorMessages } = (0, config_1.getLayoutConfig)(c, translations, mediaAssets, i);
        layoutConfigMap[c.itemId] = itemConfig;
        if (errorMessages.length) {
            validationErrorMap[c.itemId] = errorMessages.join('; ');
        }
        i += 1;
    }
    if (Object.keys(validationErrorMap).length) {
        console.error('The following errors were found');
        console.table(validationErrorMap);
        throw new Error('Something went wrong. Please look in the console for error details');
    }
    // organize media assets into batches for preloading
    const batchSize = 25;
    const batchedCorpus = (0, helpers_1.batchTrials)(defaultCorpus, batchSize);
    const batchedMediaAssets = (0, helpers_1.batchMediaAssets)(mediaAssets, batchedCorpus, ['item', 'answer', 'distractors']);
    // counter for next batch to preload (skipping the initial preload)
    let currPreloadBatch = 0;
    const initialMedia = (0, batchPreloading_1.getLeftoverAssets)(batchedMediaAssets, mediaAssets);
    const initialPreload = (0, helpers_1.createPreloadTrials)(runCat ? mediaAssets : initialMedia).default;
    const timeline = [initialPreload, initialTimeline, ...(heavyInstructions ? [instructions_1.downexInstructions1] : instructions_1.instructions)];
    const trialConfig = {
        trialType: 'audio',
        responseAllowed: true,
        promptAboveButtons: true,
        task: config.task,
        layoutConfigMap,
        terminateCat: runCat, // if running cat, stop if 4 of last 10 trials have been incorrect
    };
    const stimulusBlock = {
        timeline: [
            { ...trials_1.setupStimulus, stimulus: '' },
            (0, trials_1.practiceTransition)(),
            (0, trials_1.afcStimulusTemplate)(trialConfig),
            ifRealTrialResponse,
        ],
        // true = execute normally, false = skip
        conditional_function: () => {
            if ((0, taskStore_1.taskStore)().skipCurrentTrial) {
                (0, taskStore_1.taskStore)('skipCurrentTrial', false);
                return false;
            }
            if (runCat && taskSetup_1.cat._seMeasurement < semThreshold) {
                return false;
            }
            return true;
        },
    };
    const downexBlock = (animate) => {
        return {
            timeline: [
                { ...trials_1.setupDownex, stimulus: '' },
                (0, trials_1.practiceTransition)(),
                (0, downexStimulus_1.downexStimulus)(layoutConfigMap, animate),
                ifRealTrialResponse,
            ],
        };
    };
    const secondPhaseIndex = 5;
    let fellBack = false;
    // give older kids the downex items if they meet fall back criteria
    const fallbackBlock = {
        timeline: [
            repeatInstructions_1.repeatInstructionsMessage,
            instructions_1.downexInstructions1,
            ...downexCorpus
                .slice(0, secondPhaseIndex)
                .map((trial) => [
                { ...trials_1.fixationOnly, stimulus: '' },
                (0, downexStimulus_1.downexStimulus)(layoutConfigMap, true, trial),
                ifRealTrialResponse,
            ])
                .flat(),
            instructions_1.downexInstructions2,
            instructions_1.downexInstructions3,
            (0, trials_1.practiceTransition)(undefined, true),
            ...downexCorpus
                .slice(secondPhaseIndex)
                .map((trial) => [
                { ...trials_1.fixationOnly, stimulus: '' },
                (0, downexStimulus_1.downexStimulus)(layoutConfigMap, false, trial),
                ifRealTrialResponse,
            ])
                .flat(),
            instructions_1.downexInstructions4,
            instructions_1.downexInstructions5,
        ],
        conditional_function: () => {
            const run = (0, helpers_1.checkFallbackCriteria)() && !fellBack;
            if (run) {
                fellBack = true;
            }
            return run;
        },
    };
    function preloadBatch() {
        timeline.push((0, helpers_1.createPreloadTrials)(batchedMediaAssets[currPreloadBatch]).default);
        currPreloadBatch++;
    }
    if (runCat) {
        // seperate out corpus to get cat/non-cat blocks
        const corpora = (0, prepareCat_1.prepareCorpus)(defaultCorpus);
        // push in instruction block
        corpora.ipLight.forEach((trial) => {
            timeline.push({ ...trials_1.fixationOnly, stimulus: '' });
            timeline.push((0, trials_1.afcStimulusTemplate)(trialConfig, trial));
        });
        // push in practice transition
        if (corpora.ipLight.filter((trial) => trial.assessmentStage === 'practice_response').length > 0) {
            timeline.push((0, trials_1.practiceTransition)());
        }
        // push in starting block
        corpora.start.forEach((trial) => {
            timeline.push({ ...trials_1.fixationOnly, stimulus: '' });
            timeline.push((0, trials_1.afcStimulusTemplate)(trialConfig, trial));
            timeline.push(ifRealTrialResponse);
        });
        const numOfCatTrials = corpora.cat.length;
        (0, taskStore_1.taskStore)('totalTestTrials', numOfCatTrials);
        for (let i = 0; i < numOfCatTrials; i++) {
            timeline.push({ ...trials_1.setupStimulus, stimulus: '' });
            timeline.push((0, trials_1.afcStimulusTemplate)(trialConfig));
            timeline.push(ifRealTrialResponse);
        }
        const unnormedTrials = (0, prepareCat_1.selectNItems)(corpora.unnormed, 5);
        const unnormedBlock = {
            timeline: unnormedTrials.map((trial) => (0, trials_1.afcStimulusTemplate)(trialConfig, trial)),
        };
        timeline.push(unnormedBlock);
    }
    else {
        const numOfDownexTrials = (0, taskStore_1.taskStore)().totalDownexTrials;
        if (heavyInstructions) {
            for (let i = 0; i < numOfDownexTrials; i++) {
                const animate = i < secondPhaseIndex;
                if (i === secondPhaseIndex) {
                    timeline.push(instructions_1.downexInstructions2);
                    timeline.push(instructions_1.downexInstructions3);
                    timeline.push((0, trials_1.practiceTransition)(undefined, true));
                }
                timeline.push(downexBlock(animate));
            }
            timeline.push(instructions_1.downexInstructions4);
            timeline.push(instructions_1.downexInstructions5);
        }
        const numOfTrials = (0, taskStore_1.taskStore)().totalTrials;
        (0, taskStore_1.taskStore)('totalTestTrials', heavyInstructions ? (0, helpers_1.getRealTrials)(defaultCorpus) + (0, helpers_1.getRealTrials)(downexCorpus) : (0, helpers_1.getRealTrials)(defaultCorpus));
        const numOfInitialPracticeTrials = defaultCorpus.filter((trial) => trial.assessmentStage === 'practice_response').length;
        const fallbackIndex = numOfInitialPracticeTrials + 4;
        for (let i = 0; i < numOfTrials; i += 1) {
            if (i % batchSize === 0) {
                preloadBatch();
            }
            if (i <= fallbackIndex) {
                timeline.push(fallbackBlock);
            }
            timeline.push(stimulusBlock);
        }
    }
    (0, taskSetup_1.initializeCat)();
    timeline.push((0, trials_1.taskFinished)());
    timeline.push(trials_1.exitFullscreen);
    return { jsPsych: taskSetup_1.jsPsych, timeline };
}
